import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaPen } from 'react-icons/fa';
import useServiceDialog from '../TechnicalService/useServiceDialog';
import Navbar from '../Navbar/Navbar';
import './RentalPage.scss';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from '../Axios/axios';

const isApprovedRentalStatus = (value) =>
  value === 'Approved' || value === 'approved' || value === 1;

const isReservedStatus = (value) =>
  value === 'Reserved' || value === 'reserved' || value === 3;

const isInUseStatus = (value) =>
  value === 'InUsed' || value === 'Inused' || value === 'inused' || value === 2;

const emptyHistoryForm = {
  historyType: 'Rental',
  startDate: '',
  endDate: '',
  companyName: '',
  siteName: '',
  location: '',
  monthlyPrice: '',
  totalPrice: '',
  serviceSubject: '',
  personnelInfo: '',
  description: '',
};

const normalizeText = (value) =>
  String(value || '')
    .toLocaleLowerCase('tr-TR')
    .trim();

const parseMoneyInput = (value) => String(value || '').replace(/[^\d]/g, '');

const formatMoneyInput = (value) => {
  const raw = parseMoneyInput(value);
  if (!raw) return '';
  return `${new Intl.NumberFormat('tr-TR').format(Number(raw))} TL`;
};

const getDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const isInDateFilter = (startValue, endValue, filterStart, filterEnd) => {
  const startTime = getDateTime(startValue);
  const endTime = getDateTime(endValue) || startTime;
  const filterStartTime = getDateTime(filterStart);
  const filterEndTime = getDateTime(filterEnd);

  if (filterStartTime && endTime && endTime < filterStartTime) return false;
  if (filterEndTime && startTime && startTime > filterEndTime) return false;
  return true;
};

const includesQuery = (fields, query) => {
  const normalizedQuery = normalizeText(query);
  if (!normalizedQuery) return true;
  return fields.some((field) => normalizeText(field).includes(normalizedQuery));
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const getOneTimeItems = (rental) =>
  (rental?.lineItems || rental?.LineItems || rental?.serviceItems || []).filter((item) => {
    const type = String(item?.itemType ?? item?.ItemType ?? item?.type ?? item?.Type ?? '').toLowerCase();
    return type === '1' || type === 'onetime' || type === 'one-time';
  });

const getLineItemAmount = (item) => {
  const directAmount = toNumber(item?.amount ?? item?.Amount ?? item?.total ?? item?.Total);
  return directAmount || toNumber(item?.quantity ?? item?.Quantity ?? 1) * toNumber(item?.unitPrice ?? item?.UnitPrice);
};

const getLineItemName = (item) =>
  item?.description || item?.Description || item?.name || item?.Name || item?.serviceName || 'Ek hizmet';

const buildRentalFinancials = (rental) => {
  const monthlyRent = toNumber(rental?.rent);
  const baseMonths = toNumber(rental?.rentalPeriod);
  const invoiceCount = toNumber(rental?.invoiceInstallmentCount ?? rental?.InvoiceInstallmentCount);
  const extensionMonths = toNumber(rental?.extraPeriodMonth);
  const extensionDays = toNumber(rental?.extraPeriod);
  const extensionMonthlyRent = toNumber(rental?.extraPeriodRent) || monthlyRent;
  const rentalTotal = invoiceCount > 0
    ? monthlyRent * invoiceCount
    : monthlyRent * baseMonths +
      extensionMonthlyRent * extensionMonths +
      (extensionMonthlyRent / 30) * extensionDays;
  const serviceItems = getOneTimeItems(rental);
  const serviceTotal = serviceItems.reduce((sum, item) => sum + getLineItemAmount(item), 0);
  const commissionRate = toNumber(rental?.commissionRate) || 10;
  const rentalCommission = rentalTotal * (commissionRate / 100);

  return {
    rentalTotal,
    serviceItems,
    serviceTotal,
    commissionRate,
    rentalCommission,
    profitAmount: rentalCommission + serviceTotal,
    ownerPayable: Math.max(rentalTotal - rentalCommission, 0),
    contractTotal: rentalTotal + serviceTotal,
  };
};

const buildManualRentalFinancials = (entry) => {
  const contractTotal = toNumber(entry?.totalPrice || entry?.monthlyPrice);
  const serviceTotal = toNumber(entry?.serviceTotal ?? entry?.additionalServiceTotal);
  const rentalTotal = toNumber(entry?.rentalTotal) || Math.max(contractTotal - serviceTotal, 0);
  const commissionRate = toNumber(entry?.commissionRate) || 10;
  const rentalCommission = rentalTotal * (commissionRate / 100);

  return {
    contractTotal,
    rentalTotal,
    serviceTotal,
    commissionRate,
    rentalCommission,
    profitAmount: rentalCommission + serviceTotal,
    ownerPayable: Math.max(rentalTotal - rentalCommission, 0),
  };
};

const RentalPage = () => {
  const { id: towerCraneId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const craneFromState = location.state?.crane || null;
  const [rentalList, setRentalList] = useState([]);
  const [manualHistory, setManualHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyForm, setHistoryForm] = useState(emptyHistoryForm);
  const [historyFiles, setHistoryFiles] = useState([]);
  const [historySaving, setHistorySaving] = useState(false);
  const [editingHistory, setEditingHistory] = useState(null);
  const [historyError, setHistoryError] = useState('');
  const [historySuccess, setHistorySuccess] = useState('');
  const historyDialog = useRef(null);
  const closeHistoryForm = useCallback(() => {
    if (!historySaving) setHistoryModalOpen(false);
  }, [historySaving]);
  useServiceDialog(historyDialog, historyModalOpen, closeHistoryForm);
  const [historyFilters, setHistoryFilters] = useState({ query: '', startDate: '', endDate: '' });
  const [rentalVisibleCount, setRentalVisibleCount] = useState(3);
  const [serviceVisibleCount, setServiceVisibleCount] = useState(3);
  useEffect(() => {
    setRentalVisibleCount(3);
    setServiceVisibleCount(3);
  }, [towerCraneId, historyFilters.query, historyFilters.startDate, historyFilters.endDate]);
  const [towerCraneDetails, setTowerCraneDetails] = useState(() => (
    craneFromState
      ? {
          serialNumber: craneFromState.serialNo || 'Bilinmiyor',
          owner: craneFromState.owner || 'Bilinmiyor',
          brand: craneFromState.brand || 'Bilinmiyor',
          craneCode: craneFromState.model || 'Bilinmiyor',
          height: craneFromState.height || 'Bilinmiyor',
          maxTonnage: craneFromState.maxTonnage || 'Bilinmiyor',
          productionYear: craneFromState.productionYear || 'Bilinmiyor',
          status:
            isInUseStatus(craneFromState.status)
              ? 'Kirada'
              : craneFromState.status === 'Active' || craneFromState.status === 1
                ? 'Depoda'
                : isReservedStatus(craneFromState.status)
                  ? 'Rezerve'
                  : 'Bilinmiyor',
        }
      : null
  ));
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const canManageHistory = ['Owner', 'RentalPersonnel', 'TechnicalServiceManager'].includes(userRole);

  const manualRentalHistory = useMemo(
    () => manualHistory.filter((item) => String(item.historyType).toLowerCase() === 'rental'),
    [manualHistory]
  );

  const manualServiceHistory = useMemo(
    () => manualHistory.filter((item) => String(item.historyType).toLowerCase() === 'technicalservice'),
    [manualHistory]
  );

  const isRentalHistoryForm = historyForm.historyType === 'Rental';

  const totalContractValue = useMemo(
    () =>
      rentalList.reduce(
        (sum, rental) => sum + (rental.financials || buildRentalFinancials(rental)).contractTotal,
        0
      ) + manualRentalHistory.reduce(
        (sum, entry) => sum + buildManualRentalFinancials(entry).contractTotal,
        0
      ),
    [rentalList, manualRentalHistory]
  );

  const filteredRentalList = useMemo(
    () =>
      rentalList
        .slice()
        .reverse()
        .filter((rental) =>
          includesQuery(
            [rental.companyName, rental.workSite, rental.siteAddress, rental.rentalStartDate, rental.rentalEndDate, rental.rent],
            historyFilters.query
          ) &&
          isInDateFilter(rental.rentalStartDate, rental.rentalEndDate, historyFilters.startDate, historyFilters.endDate)
        ),
    [rentalList, historyFilters]
  );

  const filteredManualRentalHistory = useMemo(
    () =>
      manualRentalHistory.filter((entry) =>
        includesQuery(
          [entry.companyName, entry.siteName, entry.location, entry.description, entry.monthlyPrice, entry.totalPrice],
          historyFilters.query
        ) &&
        isInDateFilter(entry.startDate, entry.endDate, historyFilters.startDate, historyFilters.endDate)
      ),
    [manualRentalHistory, historyFilters]
  );

  const combinedRentalHistory = useMemo(
    () => [
      ...filteredRentalList.map((data) => ({ type: 'system', data })),
      ...filteredManualRentalHistory.map((data) => ({ type: 'manual-rental', data })),
    ].sort((a, b) => {
      const aDate = getDateTime(a.type === 'system' ? a.data.rentalStartDate : a.data.startDate) || 0;
      const bDate = getDateTime(b.type === 'system' ? b.data.rentalStartDate : b.data.startDate) || 0;
      return bDate - aDate;
    }),
    [filteredRentalList, filteredManualRentalHistory]
  );

  const filteredManualServiceHistory = useMemo(
    () =>
      manualServiceHistory.filter((entry) =>
        includesQuery(
          [entry.companyName, entry.siteName, entry.location, entry.serviceSubject, entry.personnelInfo, entry.description],
          historyFilters.query
        ) &&
        isInDateFilter(entry.startDate, entry.endDate, historyFilters.startDate, historyFilters.endDate)
      ),
    [manualServiceHistory, historyFilters]
  );

  const updateHistoryFilter = (field, value) => {
    setHistoryFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearHistoryFilters = () => {
    setHistoryFilters({ query: '', startDate: '', endDate: '' });
  };

  const updateHistoryMoney = (field, value) => {
    setHistoryForm((prev) => ({ ...prev, [field]: parseMoneyInput(value) }));
  };

  const getUserFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      const role = decodedToken?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      if (role) setUserRole(role);
    } catch (error) {
      console.error('Token çözme hatası:', error);
    }
  };

  const formatDate = (isoDateString) => {
    if (!isoDateString) return '-';
    const d = new Date(isoDateString);
    if (isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(amount) || 0) + ' TL';

  const displayValue = (value) => {
    if (!value || value === 'Bilinmiyor') return '-';
    return value;
  };

  const getFileUrl = (file) => {
    const protocol = window.location.protocol;
    const base = process.env.REACT_APP_API_BASE_URL;
    if (file?.filePath) return `${protocol}//${base}${file.filePath}`;
    return `${protocol}//${base}/uploads/${file.fileName}`;
  };

  useEffect(() => { getUserFromToken(); }, []);
  useEffect(() => {
    if (towerCraneId) {
      fetchTowerCraneDetails(towerCraneId);
      fetchRentalData(towerCraneId);
      fetchManualHistory(towerCraneId);
    }
  }, [towerCraneId]);

  const fetchTowerCraneDetails = async (id) => {
    try {
      const res = await axios.get(`/TowerCrane/GetById?id=${id}`);
      const c = res.data.data;
      if (!c) return;
      const apiStatus = c.status ?? c.Status;
      setTowerCraneDetails({
        serialNumber: c.serialNo || c.SerialNo || 'Bilinmiyor',
        owner: c.owner || c.Owner || 'Bilinmiyor',
        brand: c.brand || c.Brand || 'Bilinmiyor',
        craneCode: c.model || c.Model || 'Bilinmiyor',
        height: c.height || c.Height || 'Bilinmiyor',
        maxTonnage: c.maxTonnage || c.MaxTonnage || 'Bilinmiyor',
        productionYear: c.productionYear || c.ProductionYear || 'Bilinmiyor',
        status:
          isInUseStatus(apiStatus)
            ? 'Kirada'
            : apiStatus === 'Active' || apiStatus === 1
              ? 'Depoda'
              : isReservedStatus(apiStatus)
                ? 'Rezerve'
                : 'Bilinmiyor',
      });
    } catch (e) { console.error('Kule vinç bilgileri alınamadı:', e); }
  };

  const fetchRentalData = async (id) => {
    try {
      const [res, reserveRes, detailedRentalsRes] = await Promise.all([
        axios.get(`/Rental/GetByTowerCrane/${id}`, { params: { includeLineItems: true } }),
        axios.get('/Reserve').catch(() => ({ data: { data: [] } })),
        axios.get('/Rental', { params: { includeLineItems: true } }).catch(() => ({ data: { data: [] } })),
      ]);
      const detailedRentals = detailedRentalsRes?.data?.data || [];
      const allRentals = (res.data.data || []).map((rental) => {
        const detailedRental = detailedRentals.find((item) => Number(item?.id) === Number(rental?.id));
        return detailedRental ? { ...rental, ...detailedRental } : rental;
      });
      const approvedRentals = allRentals.filter((r) => isApprovedRentalStatus(r?.serviceStatus));
      const activeApprovedRental = approvedRentals.find((r) => r?.isActive);
      const activeReserve = (reserveRes?.data?.data || []).find(
        (reserve) =>
          reserve?.towerCraneId === Number(id) &&
          String(reserve?.reserveType || '').toLowerCase() === 'reserved'
      );

      setTowerCraneDetails((prev) =>
        prev ? { ...prev, status: activeApprovedRental ? 'Kirada' : activeReserve ? 'Rezerve' : 'Depoda' } : prev
      );

      if (!allRentals.length) {
        setRentalList([]);
        return;
      }

      const enrichedRentals = allRentals.map((r) => {
        const financials = buildRentalFinancials(r);
        return { ...r, financials, netRevenue: financials.profitAmount };
      });
      await fetchCompanyAndSiteDetails(enrichedRentals);
    } catch (e) {
      console.error('Kiralama verileri alınamadı:', e);
    }
  };

  const fetchManualHistory = async (id) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`/CraneHistory/${id}`, { skipCache: true });
      setManualHistory(res?.data?.data || []);
    } catch (error) {
      console.error('Vinç geçmişi alınamadı:', error);
      setManualHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchCompanyAndSiteDetails = async (rentalDataArray) => {
    try {
      const updated = await Promise.all(
        rentalDataArray.map(async (r) => {
          let companyName = 'Bilinmiyor';
          let siteAddress = 'Bilinmiyor';

          if (r.rentingCompanyId) {
            try {
              const cRes = await axios.get(`/Company/GetById?id=${r.rentingCompanyId}`);
              companyName = cRes?.data?.data?.name || 'Bilinmiyor';
            } catch (error) {
              console.error('Kiralayan firma bilgisi alınamadı:', error);
            }
          }
          if (r.workSiteId) {
            try {
              const sRes = await axios.get(`/Worksite/GetById?id=${r.workSiteId}`);
              siteAddress = sRes?.data?.data?.address || 'Bilinmiyor';
            } catch (error) {
              console.error('Şantiye bilgisi alınamadı:', error);
            }
          }
          return { ...r, companyName, siteAddress };
        })
      );
      setRentalList(updated);
    } catch (e) {
      console.error('Şirket veya şantiye bilgileri alınamadı:', e);
    }
  };

  const openHistoryModal = (historyType = 'Rental') => {
    setEditingHistory(null);
    setHistoryError('');
    setHistorySuccess('');
    setHistoryForm({ ...emptyHistoryForm, historyType });
    setHistoryFiles([]);
    setHistoryModalOpen(true);
  };

  const editHistory = (entry) => {
    const filled = Object.fromEntries(Object.keys(emptyHistoryForm).map((key) => [key, entry[key] ?? '']));
    filled.startDate = String(filled.startDate).slice(0, 10);
    filled.endDate = String(filled.endDate).slice(0, 10);
    setEditingHistory(entry);
    setHistoryForm(filled);
    setHistoryFiles([]);
    setHistoryError('');
    setHistorySuccess('');
    setSelectedHistory(null);
    setHistoryModalOpen(true);
  };

  const saveHistory = async (event) => {
    event.preventDefault();
    if (historySaving) return;
    setHistoryError('');
    if (historyForm.startDate && historyForm.endDate && historyForm.endDate < historyForm.startDate) {
      setHistoryError('Bitiş tarihi başlangıç tarihinden önce olamaz.');
      return;
    }
    setHistorySaving(true);
    try {
      if (editingHistory) {
        const original = Object.fromEntries(Object.keys(emptyHistoryForm)
          .filter((key) => key !== 'historyType').map((key) => [key, editingHistory[key] ?? null]));
        const values = Object.fromEntries(Object.entries(historyForm).map(([key, value]) => {
          if (['monthlyPrice', 'totalPrice'].includes(key)) return [key, value === '' ? null : Number(value)];
          if (['startDate', 'endDate'].includes(key) && value && value === String(editingHistory[key] || '').slice(0, 10)) return [key, editingHistory[key]];
          return [key, value === '' ? null : value];
        }));
        const response = await axios.put(`/CraneHistory/${editingHistory.id}`, {
          ...values, towerCraneId: editingHistory.towerCraneId, original,
        });
        if (!response?.data?.isSuccess) throw new Error('Arşiv kaydı güncellenemedi.');
        axios.clearGetCache(`/CraneHistory/${towerCraneId}`);
        await fetchManualHistory(towerCraneId);
        setHistoryModalOpen(false);
        setEditingHistory(null);
        setHistorySuccess('Arşiv kaydı güncellendi. Ekli belgeler korundu.');
        return;
      }
      const formData = new FormData();
      formData.append('towerCraneId', towerCraneId);
      Object.entries(historyForm).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          const parsedValue = ['monthlyPrice', 'totalPrice'].includes(key) ? parseMoneyInput(value) : value;
          formData.append(key, parsedValue);
        }
      });
      historyFiles.forEach((file) => formData.append('files', file));

      const response = await axios.post('/CraneHistory', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response?.data?.isSuccess) {
        setHistoryModalOpen(false);
        await fetchManualHistory(towerCraneId);
      } else throw new Error('Arşiv kaydı eklenemedi.');
    } catch (error) {
      const messages = error.response?.data?.messages;
      setHistoryError(Array.isArray(messages) ? messages.join(' ') : 'Kayıt kaydedilemedi. Bilgileriniz formda duruyor; kontrol edip tekrar deneyin.');
    } finally {
      setHistorySaving(false);
    }
  };

  const navigateToEditPage = (rentalId) => navigate(`/rentaledit/${rentalId}`);

  const renderHistoryFiles = (files = []) => {
    if (!files.length) return null;
    return (
      <div className="history-files">
        {files.map((file) => (
          <a key={file.id || file.fileName} href={getFileUrl(file)} target="_blank" rel="noopener noreferrer">
            {file.originalFileName || file.fileName || 'Dosya'}
          </a>
        ))}
      </div>
    );
  };

  return (
    <div className="rental-page">
      <Navbar />
      {historySuccess && <div role="status" style={{ padding: '12px 20px', background: '#eaf7ef', color: '#17643b' }}>{historySuccess}</div>}

      <section className="rental-hero">
        <div>
          <span className="eyebrow">Vinç Kartı</span>
          <h2>Kiralama Bilgileri</h2>
          <p>Aktif kayıtlar, geçmiş kiralamalar, teknik servis formları ve yüklenen belgeler tek vinç altında tutulur.</p>
        </div>
        {canManageHistory && (
          <div className="hero-actions">
            <button type="button" onClick={() => openHistoryModal('Rental')}>Kiralama Geçmişi Ekle</button>
            <button type="button" onClick={() => openHistoryModal('TechnicalService')}>Servis Geçmişi Ekle</button>
          </div>
        )}
      </section>

      {towerCraneDetails ? (
        <section className="crane-info">
          <div className="section-title">
            <div>
              <span>Kule Vinç Bilgileri</span>
              <h3>{towerCraneDetails.serialNumber}</h3>
            </div>
            <strong className="status-pill">{towerCraneDetails.status}</strong>
          </div>
          <div className="crane-details">
            <div><span>Sahibi</span><strong>{displayValue(towerCraneDetails.owner)}</strong></div>
            <div><span>Marka</span><strong>{displayValue(towerCraneDetails.brand)}</strong></div>
            <div><span>Vinç Kodu</span><strong>{displayValue(towerCraneDetails.craneCode)}</strong></div>
            <div><span>Yükseklik</span><strong>{displayValue(towerCraneDetails.height) === '-' ? '-' : `${towerCraneDetails.height}m`}</strong></div>
            <div><span>Maks. Tonaj</span><strong>{displayValue(towerCraneDetails.maxTonnage)}</strong></div>
            <div><span>Tüm Kiralamalar Toplamı</span><strong>{formatCurrency(totalContractValue)}</strong></div>
          </div>
        </section>
      ) : (
        <p className="empty-state">Kule vinç bilgisi bulunamadı.</p>
      )}

      <section className="history-grid">
        <div className="history-card">
          <div className="section-title">
            <div>
              <span>Kiralama Arşivi</span>
              <h3>Kiralama Geçmişi</h3>
            </div>
            <strong>{rentalList.length + manualRentalHistory.length} kayıt</strong>
          </div>

          <div className="history-filters">
            <input
              value={historyFilters.query}
              onChange={(e) => updateHistoryFilter('query', e.target.value)}
              placeholder="Firma, şantiye, konum veya not ara"
            />
            <input
              type="date"
              value={historyFilters.startDate}
              onChange={(e) => updateHistoryFilter('startDate', e.target.value)}
            />
            <input
              type="date"
              value={historyFilters.endDate}
              onChange={(e) => updateHistoryFilter('endDate', e.target.value)}
            />
            <button type="button" onClick={clearHistoryFilters}>Temizle</button>
          </div>

          {historyLoading ? (
            <p className="empty-state">Geçmiş yükleniyor...</p>
          ) : rentalList.length + manualRentalHistory.length === 0 ? (
            <p className="empty-state">Bu vinç için kiralama geçmişi bulunmuyor.</p>
          ) : filteredRentalList.length + filteredManualRentalHistory.length === 0 ? (
            <p className="empty-state">Filtreye uygun kiralama geçmişi bulunmuyor.</p>
          ) : (
            <div className="history-list">
              {combinedRentalHistory.slice(0, rentalVisibleCount).map(({ type, data }, index) => {
                const isSystem = type === 'system';
                const startDate = isSystem ? data.rentalStartDate : data.startDate;
                const endDate = isSystem ? data.rentalEndDate : data.endDate;
                const siteName = isSystem ? data.workSite : data.siteName;
                const locationName = isSystem ? data.siteAddress : data.location;
                const contractTotal = isSystem
                  ? data.financials?.contractTotal
                  : buildManualRentalFinancials(data).contractTotal;

                return (
                  <article
                    key={`${type}-${data.id || index}`}
                    className={`history-item ${isSystem ? 'system' : 'manual'}`}
                    onClick={() => setSelectedHistory({ type, data })}
                  >
                    <div>
                      <strong>{data.companyName || 'Firma girilmemiş'}</strong>
                      <span>{formatDate(startDate)} - {formatDate(endDate)}</span>
                      <em>{siteName || 'Şantiye bilgisi yok'} / {locationName || 'Konum yok'}</em>
                    </div>
                    <div className="history-item-meta">
                      <small className={`history-source-badge ${isSystem ? 'system' : 'manual'}`}>
                        {isSystem ? 'Sistem' : 'Manuel arşiv'}
                      </small>
                      <b>{formatCurrency(contractTotal)}</b>
                      <em className="amount-caption">Toplam sözleşme</em>
                      <div className="archive-record-actions">
                        <button type="button" onClick={(event) => { event.stopPropagation(); setSelectedHistory({ type, data }); }}>Detayı Gör</button>
                        {!isSystem && canManageHistory && <button type="button" className="archive-edit-action" onClick={(event) => { event.stopPropagation(); editHistory(data); }}><FaPen aria-hidden="true" /> Düzenle</button>}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          {!historyLoading && combinedRentalHistory.length > rentalVisibleCount && (
            <button type="button" className="archive-show-more" onClick={() => setRentalVisibleCount((count) => count + 3)}>
              Daha fazla göster <span>({combinedRentalHistory.length - rentalVisibleCount} kayıt daha)</span>
            </button>
          )}
        </div>

        <div className="history-card">
          <div className="section-title">
            <div>
              <span>Servis Arşivi</span>
              <h3>Teknik Servis Geçmişi</h3>
            </div>
            <strong>{manualServiceHistory.length} kayıt</strong>
          </div>

          <div className="history-filters compact">
            <input
              value={historyFilters.query}
              onChange={(e) => updateHistoryFilter('query', e.target.value)}
              placeholder="Servis konusu, firma veya konum ara"
            />
            <button type="button" onClick={clearHistoryFilters}>Temizle</button>
          </div>

          {historyLoading ? (
            <p className="empty-state">Geçmiş yükleniyor...</p>
          ) : manualServiceHistory.length === 0 ? (
            <p className="empty-state">Bu vinç için manuel teknik servis geçmişi bulunmuyor.</p>
          ) : filteredManualServiceHistory.length === 0 ? (
            <p className="empty-state">Filtreye uygun teknik servis geçmişi bulunmuyor.</p>
          ) : (
            <div className="history-list">
              {filteredManualServiceHistory.slice(0, serviceVisibleCount).map((entry) => (
                <article
                  key={`manual-service-${entry.id}`}
                  className="history-item service"
                  onClick={() => setSelectedHistory({ type: 'service', data: entry })}
                >
                  <div>
                    <strong>{entry.serviceSubject || 'Servis kaydı'}</strong>
                    <span>{formatDate(entry.startDate)} - {formatDate(entry.endDate)}</span>
                    <em>{entry.companyName || 'Firma yok'} / {entry.location || 'Konum yok'}</em>
                  </div>
                  <div className="history-item-meta">
                    <small className="history-source-badge service">Manuel servis</small>
                    <b>{formatCurrency(entry.totalPrice)}</b>
                    <em className="amount-caption">Hizmet toplamı</em>
                    <div className="archive-record-actions">
                      <button type="button" onClick={(event) => { event.stopPropagation(); setSelectedHistory({ type: 'service', data: entry }); }}>Detayı Gör</button>
                      {canManageHistory && <button type="button" className="archive-edit-action" onClick={(event) => { event.stopPropagation(); editHistory(entry); }}><FaPen aria-hidden="true" /> Düzenle</button>}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
          {!historyLoading && filteredManualServiceHistory.length > serviceVisibleCount && (
            <button type="button" className="archive-show-more" onClick={() => setServiceVisibleCount((count) => count + 3)}>
              Daha fazla göster <span>({filteredManualServiceHistory.length - serviceVisibleCount} kayıt daha)</span>
            </button>
          )}
        </div>
      </section>

      {selectedHistory && (
        <div className="history-modal-backdrop" onClick={() => setSelectedHistory(null)}>
          <div className="history-modal history-detail-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <div>
                <span>{selectedHistory.type === 'service' ? 'Teknik Servis Arşivi' : 'Kiralama Arşivi'}</span>
                <h3>
                  {selectedHistory.type === 'service'
                    ? selectedHistory.data.serviceSubject || 'Servis kaydı'
                    : selectedHistory.data.companyName || 'Kiralama detayı'}
                </h3>
              </div>
              <button type="button" aria-label="Kapat" onClick={() => setSelectedHistory(null)}>×</button>
            </div>

            {selectedHistory.type === 'system' ? (() => {
              const rental = selectedHistory.data;
              const financials = rental.financials || buildRentalFinancials(rental);
              return (
                <div className="history-detail-content">
                  <div className="detail-summary-grid">
                    <div><span>Star Crane Kazancı</span><strong>{formatCurrency(financials.profitAmount)}</strong></div>
                    <div><span>Kira Toplamı</span><strong>{formatCurrency(financials.rentalTotal)}</strong></div>
                    <div><span>Toplam Sözleşme</span><strong>{formatCurrency(financials.contractTotal)}</strong></div>
                    <div><span>Mal Sahibi Payı</span><strong>{formatCurrency(financials.ownerPayable)}</strong></div>
                  </div>

                  <section className="detail-section">
                    <h4>Kiralama Bilgileri</h4>
                    <div className="detail-info-grid">
                      <div><span>Firma</span><strong>{rental.companyName || '-'}</strong></div>
                      <div><span>Şantiye</span><strong>{rental.workSite || '-'}</strong></div>
                      <div><span>Adres</span><strong>{rental.siteAddress || '-'}</strong></div>
                      <div><span>Tarih</span><strong>{formatDate(rental.rentalStartDate)} - {formatDate(rental.rentalEndDate)}</strong></div>
                      <div><span>Aylık Kira</span><strong>{formatCurrency(rental.rent)}</strong></div>
                      <div><span>Süre</span><strong>{rental.rentalPeriod || 0} ay{rental.extraPeriodMonth ? ` + ${rental.extraPeriodMonth} ay` : ''}{rental.extraPeriod ? ` + ${rental.extraPeriod} gün` : ''}</strong></div>
                    </div>
                  </section>

                  <section className="detail-section">
                    <h4>Kazanç Dağılımı</h4>
                    <div className="detail-info-grid financial">
                      <div><span>Kira Komisyonu (%{financials.commissionRate})</span><strong>{formatCurrency(financials.rentalCommission)}</strong></div>
                      <div><span>Ek Hizmet Toplamı</span><strong>{formatCurrency(financials.serviceTotal)}</strong></div>
                    </div>
                    {financials.serviceItems.length > 0 ? (
                      <div className="service-line-list">
                        {financials.serviceItems.map((item, index) => (
                          <div key={item.id || item.Id || index}>
                            <span>{getLineItemName(item)}</span>
                            <strong>{formatCurrency(getLineItemAmount(item))}</strong>
                          </div>
                        ))}
                      </div>
                    ) : <p className="detail-empty">Ek hizmet kalemi bulunmuyor.</p>}
                  </section>

                  <section className="detail-section">
                    <h4>Belgeler ve Notlar</h4>
                    <div className="documents-row">
                      {(rental.files || []).map((file, index) => (
                        <a key={file.id || index} href={getFileUrl(file)} target="_blank" rel="noopener noreferrer" className="document">
                          {({ Offer: 'Teklif Dosyası', Contract: 'Sözleşme Dosyası', ServiceForm: 'Servis Formu', Image: 'Görsel' })[file.fileType] || 'Dosya'}
                        </a>
                      ))}
                    </div>
                    <div className="notes-container">
                      {(rental.notes || []).length > 0
                        ? rental.notes.map((note, index) => <div key={note.id || index} className="note-item">{note.note}</div>)
                        : <p className="detail-empty">Not bulunmuyor.</p>}
                    </div>
                  </section>

                  <div className="modal-actions">
                    <button type="button" onClick={() => setSelectedHistory(null)}>Kapat</button>
                    {userRole === 'Owner' && (
                      <button type="button" onClick={() => navigateToEditPage(rental.id)}>Düzenle</button>
                    )}
                  </div>
                </div>
              );
            })() : selectedHistory.type === 'manual-rental' ? (() => {
              const entry = selectedHistory.data;
              const financials = buildManualRentalFinancials(entry);
              return (
                <div className="history-detail-content">
                  <div className="manual-record-notice">
                    <strong>Manuel Arşiv Kaydı</strong>
                    <span>Yalnızca vinç geçmişinde gösterilir; fatura ve aktif kiralama süreçlerine dahil edilmez.</span>
                  </div>
                  <div className="detail-summary-grid">
                    <div><span>Star Crane Kazancı</span><strong>{formatCurrency(financials.profitAmount)}</strong></div>
                    <div><span>Kira Toplamı</span><strong>{formatCurrency(financials.rentalTotal)}</strong></div>
                    <div><span>Toplam Sözleşme</span><strong>{formatCurrency(financials.contractTotal)}</strong></div>
                    <div><span>Mal Sahibi Payı</span><strong>{formatCurrency(financials.ownerPayable)}</strong></div>
                  </div>
                  <section className="detail-section">
                    <h4>Kiralama Bilgileri</h4>
                    <div className="detail-info-grid">
                      <div><span>Firma</span><strong>{entry.companyName || '-'}</strong></div>
                      <div><span>Şantiye</span><strong>{entry.siteName || '-'}</strong></div>
                      <div><span>Konum</span><strong>{entry.location || '-'}</strong></div>
                      <div><span>Tarih</span><strong>{formatDate(entry.startDate)} - {formatDate(entry.endDate)}</strong></div>
                      <div><span>Aylık Kira</span><strong>{formatCurrency(entry.monthlyPrice)}</strong></div>
                      <div><span>Kayıt Türü</span><strong>Manuel arşiv</strong></div>
                    </div>
                  </section>
                  <section className="detail-section">
                    <h4>Kazanç Dağılımı</h4>
                    <div className="detail-info-grid financial">
                      <div><span>Kira Komisyonu (%{financials.commissionRate})</span><strong>{formatCurrency(financials.rentalCommission)}</strong></div>
                      <div><span>Ek Hizmet Toplamı</span><strong>{formatCurrency(financials.serviceTotal)}</strong></div>
                    </div>
                  </section>
                  {entry.description && <p className="detail-description">{entry.description}</p>}
                  {renderHistoryFiles(entry.files)}
                  <div className="modal-actions">
                    <button type="button" onClick={() => setSelectedHistory(null)}>Kapat</button>
                    {canManageHistory && <button type="button" onClick={() => editHistory(entry)}><FaPen aria-hidden="true" /> Düzenle</button>}
                  </div>
                </div>
              );
            })() : (
              <div className="history-detail-content">
                <div className="manual-record-notice service">
                  <strong>Manuel Teknik Servis Arşivi</strong>
                  <span>Yalnızca servis geçmişinde gösterilir; fatura planı oluşturmaz.</span>
                </div>
                <div className="detail-summary-grid service-summary">
                  <div><span>Star Crane Hizmet Geliri</span><strong>{formatCurrency(selectedHistory.data.totalPrice)}</strong></div>
                  <div><span>Toplam Hizmet Tutarı</span><strong>{formatCurrency(selectedHistory.data.totalPrice)}</strong></div>
                </div>
                <div className="detail-info-grid">
                  <div><span>Başlangıç</span><strong>{formatDate(selectedHistory.data.startDate)}</strong></div>
                  <div><span>Bitiş</span><strong>{formatDate(selectedHistory.data.endDate)}</strong></div>
                  <div><span>Firma</span><strong>{selectedHistory.data.companyName || '-'}</strong></div>
                  <div><span>Şantiye</span><strong>{selectedHistory.data.siteName || '-'}</strong></div>
                  <div><span>Konum</span><strong>{selectedHistory.data.location || '-'}</strong></div>
                  <div><span>Personel / Ekip</span><strong>{selectedHistory.data.personnelInfo || '-'}</strong></div>
                </div>
                {selectedHistory.data.description && <p className="detail-description">{selectedHistory.data.description}</p>}
                {renderHistoryFiles(selectedHistory.data.files)}
                <div className="modal-actions">
                  <button type="button" onClick={() => setSelectedHistory(null)}>Kapat</button>
                  {canManageHistory && <button type="button" onClick={() => editHistory(selectedHistory.data)}><FaPen aria-hidden="true" /> Düzenle</button>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {historyModalOpen && (
        <div className="history-modal-backdrop" onClick={closeHistoryForm}>
          <form ref={historyDialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="history-form-title" className="history-modal" onSubmit={saveHistory} onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <div>
                <span>Manuel Geçmiş</span>
                <h3 id="history-form-title">{isRentalHistoryForm ? 'Kiralama' : 'Teknik Servis'} {editingHistory ? 'Arşivini Düzenle' : 'Geçmişi Ekle'}</h3>
              </div>
              <button type="button" disabled={historySaving} onClick={closeHistoryForm} aria-label="Kapat">×</button>
            </div>

            <div className="history-type-summary">
              <div>
                <span>Kayıt Alanı</span>
                <strong>{isRentalHistoryForm ? 'Kiralama Arşivi' : 'Teknik Servis Arşivi'}</strong>
              </div>
              <p>
                {editingHistory ? 'Mevcut kayıt güncellenir. Ekli belgeler korunur; aktif kiralama ve faturalar etkilenmez.' : isRentalHistoryForm
                  ? 'Bu kayıt seçili vincin kiralama geçmişine eklenecek.'
                  : 'Bu kayıt seçili vincin teknik servis geçmişine eklenecek.'}
              </p>
            </div>

            {historyError && <div role="alert" style={{ margin: '12px 0', padding: 12, background: '#fff0f0', color: '#a32323', borderRadius: 8 }}>{historyError}</div>}
            <fieldset disabled={historySaving} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
            <div className="form-grid">
              <label>
                Başlangıç
                <input type="date" value={historyForm.startDate} onChange={(e) => setHistoryForm((prev) => ({ ...prev, startDate: e.target.value }))} />
              </label>
              <label>
                Bitiş
                <input type="date" value={historyForm.endDate} onChange={(e) => setHistoryForm((prev) => ({ ...prev, endDate: e.target.value }))} />
              </label>
              <label>
                Firma
                <input value={historyForm.companyName} onChange={(e) => setHistoryForm((prev) => ({ ...prev, companyName: e.target.value }))} placeholder="Firma adı" />
              </label>
              <label>
                Şantiye
                <input value={historyForm.siteName} onChange={(e) => setHistoryForm((prev) => ({ ...prev, siteName: e.target.value }))} placeholder="Şantiye adı" />
              </label>
              <label>
                Konum
                <input value={historyForm.location} onChange={(e) => setHistoryForm((prev) => ({ ...prev, location: e.target.value }))} placeholder="İl / ilçe veya adres" />
              </label>
              {isRentalHistoryForm && (
                <label>
                  Aylık Tutar (TL)
                  <input
                    type={editingHistory ? 'number' : 'text'} min="0" step="0.01"
                    inputMode={editingHistory ? 'decimal' : 'numeric'}
                    value={editingHistory ? historyForm.monthlyPrice : formatMoneyInput(historyForm.monthlyPrice)}
                    onChange={(e) => editingHistory ? setHistoryForm((p) => ({ ...p, monthlyPrice: e.target.value })) : updateHistoryMoney('monthlyPrice', e.target.value)}
                    placeholder="0 TL"
                  />
                </label>
              )}
              <label>
                Toplam Tutar (TL)
                <input
                  type={editingHistory ? 'number' : 'text'} min="0" step="0.01"
                  inputMode={editingHistory ? 'decimal' : 'numeric'}
                  value={editingHistory ? historyForm.totalPrice : formatMoneyInput(historyForm.totalPrice)}
                  onChange={(e) => editingHistory ? setHistoryForm((p) => ({ ...p, totalPrice: e.target.value })) : updateHistoryMoney('totalPrice', e.target.value)}
                  placeholder="0 TL"
                />
              </label>
              {!isRentalHistoryForm && (
                <>
                  <label>
                    Servis Konusu
                    <input value={historyForm.serviceSubject} onChange={(e) => setHistoryForm((prev) => ({ ...prev, serviceSubject: e.target.value }))} placeholder="Bakım, montaj, arıza..." />
                  </label>
                  <label>
                    Personel / Ekip
                    <input value={historyForm.personnelInfo} onChange={(e) => setHistoryForm((prev) => ({ ...prev, personnelInfo: e.target.value }))} placeholder="Varsa ekip bilgisi" />
                  </label>
                </>
              )}
              {editingHistory ? <div className="wide"><strong>Ekli Belgeler</strong>{renderHistoryFiles(editingHistory.files)}<p>Mevcut belgeler değiştirilmeden korunur.</p></div> : <label className="wide">
                Dosyalar
                <input type="file" multiple onChange={(e) => setHistoryFiles(Array.from(e.target.files || []))} />
              </label>}
              <label className="wide">
                Açıklama
                <textarea value={historyForm.description} onChange={(e) => setHistoryForm((prev) => ({ ...prev, description: e.target.value }))} placeholder={isRentalHistoryForm ? 'Sözleşme, ödeme notu veya kiralama açıklaması' : 'Servis formu, işlem notu veya ekip açıklaması'} />
              </label>
            </div>
            </fieldset>

            <div className="modal-actions">
              <button type="button" disabled={historySaving} onClick={closeHistoryForm}>Vazgeç</button>
              <button type="submit" disabled={historySaving}>{historySaving ? 'Kaydediliyor...' : editingHistory ? 'Değişiklikleri Kaydet' : 'Kaydet'}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default RentalPage;
